import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth";
import { useContent } from "@/lib/content";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, Utensils, CalendarCheck, FileText, Briefcase, Type, Image, Upload, X as XIcon, ExternalLink, LayoutDashboard, BarChart3, Star, Monitor, Smartphone, Tablet } from "lucide-react";
import RichEditor from "@/components/RichEditor";

const resolveImg = (u) => {
  if (!u) return null;
  if (u.startsWith("http")) return u;
  return `${API.replace(/\/api$/, "")}${u}`;
};

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [tab, setTab] = useState("summary");

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#5C5C59]">…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen flex bg-[#FAF9F6]">
      <aside className="w-64 bg-[#1A1A1A] text-[#F3F1EC] flex flex-col">
        <div className="p-6 border-b border-white/10">
          <a href="/" className="block">
            <div className="font-script text-3xl leading-tight">Harry's</div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#A6A6A0] mt-1.5 font-medium">
              Salotto &amp; Cucina
            </div>
          </a>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#A6A6A0] mt-3">Admin</div>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          <SideBtn icon={LayoutDashboard} active={tab === "summary"} onClick={() => setTab("summary")} testid="tab-summary">
            Riepilogo
          </SideBtn>
          <SideBtn icon={BarChart3} active={tab === "stats"} onClick={() => setTab("stats")} testid="tab-stats">
            Statistiche
          </SideBtn>
          <SideBtn icon={Utensils} active={tab === "menu"} onClick={() => setTab("menu")} testid="tab-menu">
            {t("admin.menu")}
          </SideBtn>
          <SideBtn icon={LayoutDashboard} active={tab === "categories"} onClick={() => setTab("categories")} testid="tab-categories">
            Categorie menu
          </SideBtn>
          <SideBtn icon={CalendarCheck} active={tab === "reservations"} onClick={() => setTab("reservations")} testid="tab-reservations">
            {t("admin.reservations")}
          </SideBtn>
          <SideBtn icon={FileText} active={tab === "quotes"} onClick={() => setTab("quotes")} testid="tab-quotes">
            {t("admin.quotes")}
          </SideBtn>
          <SideBtn icon={Briefcase} active={tab === "careers"} onClick={() => setTab("careers")} testid="tab-careers">
            {t("admin.careers")}
          </SideBtn>
          <SideBtn icon={Type} active={tab === "content"} onClick={() => setTab("content")} testid="tab-content">
            Contenuti
          </SideBtn>
          <SideBtn icon={Image} active={tab === "gallery"} onClick={() => setTab("gallery")} testid="tab-gallery">
            Galleria
          </SideBtn>
          <SideBtn icon={Image} active={tab === "site-images"} onClick={() => setTab("site-images")} testid="tab-site-images">
            Immagini sito
          </SideBtn>
        </nav>
        <button
          data-testid="admin-logout"
          onClick={async () => { await logout(); nav("/"); }}
          className="m-4 p-3 flex items-center gap-2 text-sm text-[#A6A6A0] hover:text-white border border-white/10"
        >
          <LogOut className="w-4 h-4" strokeWidth={1.5} /> {t("admin.logout")}
        </button>
      </aside>

      <main className="flex-1 p-8 sm:p-12 overflow-auto">
        {tab === "summary" && <SummaryAdmin />}
        {tab === "stats" && <StatsAdmin />}
        {tab === "menu" && <MenuAdmin t={t} />}
        {tab === "categories" && <CategoriesAdmin />}
        {tab === "reservations" && <ReservationsAdmin t={t} />}
        {tab === "quotes" && <QuotesAdmin t={t} />}
        {tab === "careers" && <CareersAdmin t={t} />}
        {tab === "content" && <ContentAdmin />}
        {tab === "gallery" && <GalleryAdmin />}
        {tab === "site-images" && <SiteImagesAdmin />}
      </main>
    </div>
  );
}

function SideBtn({ icon: Icon, children, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
        active ? "bg-white/10 text-white" : "text-[#A6A6A0] hover:text-white"
      }`}
    >
      <Icon className="w-4 h-4" strokeWidth={1.5} />
      {children}
    </button>
  );
}

function MenuAdmin({ t }) {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/menu").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/categories/all").then((r) => setCats(r.data)).catch(() => setCats([]));
  }, []);
  const catLabel = (key) => cats.find((c) => c.key === key)?.label_it || key;

  const onDelete = async (id) => {
    if (!window.confirm(t("admin.confirm"))) return;
    await api.delete(`/menu/${id}`);
    toast.success("OK");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-4xl tracking-tight">{t("admin.menu")}</h1>
        <button
          data-testid="add-menu-item"
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="px-5 py-3 bg-[#8B3A3A] text-white hover:bg-[#6C2A2A] inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" strokeWidth={1.5} /> {t("admin.addItem")}
        </button>
      </div>

      {showForm && (
        <MenuForm
          initial={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
          t={t}
        />
      )}

      <div className="bg-white border border-gray-200 rounded">
        <table className="w-full text-sm">
          <thead className="border-b border-gray-200 text-xs tracking-wider uppercase text-[#5C5C59]">
            <tr>
              <th className="px-4 py-3 text-left">Img</th>
              <th className="px-4 py-3 text-left">Cat.</th>
              <th className="px-4 py-3 text-left">Nome</th>
              <th className="px-4 py-3 text-right">€</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-b border-gray-100" data-testid={`admin-menu-row-${it.id}`}>
                <td className="px-4 py-2">
                  {it.image_url ? (
                    <img src={resolveImg(it.image_url)} alt="" className="w-12 h-12 object-cover rounded" />
                  ) : (
                    <div className="w-12 h-12 bg-gray-100 rounded" />
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-[#5C5C59]">{catLabel(it.category)}</td>
                <td className="px-4 py-3 font-medium">{it.name_it}</td>
                <td className="px-4 py-3 text-right">{it.price.toFixed(2)}</td>
                <td className="px-4 py-3 flex gap-2 justify-end">
                  <button onClick={() => { setEditing(it); setShowForm(true); }} data-testid={`edit-${it.id}`} className="p-2 hover:bg-gray-100">
                    <Pencil className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                  <button onClick={() => onDelete(it.id)} data-testid={`delete-${it.id}`} className="p-2 hover:bg-red-50 text-red-700">
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MenuForm({ initial, onClose, onSaved, t }) {
  const [cats, setCats] = useState([]);
  useEffect(() => {
    api.get("/categories/all").then((r) => setCats(r.data)).catch(() => setCats([]));
  }, []);
  const [f, setF] = useState(initial || {
    category: "antipasti_carne",
    name_it: "", name_en: "", name_de: "",
    description_it: "", description_en: "", description_de: "",
    price: 0, is_available: true, order: 0, image_url: null,
  });
  const [uploading, setUploading] = useState(false);
  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setF((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setF((p) => ({ ...p, image_url: data.url }));
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error("Errore upload");
    } finally {
      setUploading(false);
    }
  };
  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...f, price: Number(f.price), order: Number(f.order) };
    if (initial) await api.put(`/menu/${initial.id}`, payload);
    else await api.post("/menu", payload);
    toast.success("OK");
    onSaved();
  };
  const inp = "w-full border border-gray-300 px-3 py-2 rounded text-sm";
  return (
    <form onSubmit={submit} className="bg-white border border-gray-200 rounded p-6 mb-6" data-testid="menu-form">
      <div className="grid sm:grid-cols-3 gap-4">
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-[#5C5C59]">{t("admin.category")}</span>
          <select name="category" value={f.category} onChange={onChange} className={inp + " mt-1"}>
            {cats.length === 0 ? (
              <option value={f.category}>{f.category}</option>
            ) : (
              cats.map((c) => <option key={c.key} value={c.key}>{c.label_it}</option>)
            )}
          </select>
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-[#5C5C59]">{t("admin.price")} (€)</span>
          <input name="price" type="number" step="0.5" value={f.price} onChange={onChange} className={inp + " mt-1"} required />
        </label>
        <label className="flex items-center gap-2 mt-6">
          <input type="checkbox" name="is_available" checked={f.is_available} onChange={onChange} />
          <span className="text-sm">{t("admin.available")}</span>
        </label>
      </div>

      {/* Image upload */}
      <div className="mt-5">
        <span className="text-xs uppercase tracking-wider text-[#5C5C59]">Immagine piatto</span>
        <div className="mt-2 flex items-center gap-4">
          {f.image_url ? (
            <div className="relative">
              <img src={resolveImg(f.image_url)} alt="" className="w-24 h-24 object-cover rounded border border-gray-200" />
              <button
                type="button"
                onClick={() => setF((p) => ({ ...p, image_url: null }))}
                className="absolute -top-2 -right-2 bg-white border border-gray-300 rounded-full p-1 hover:bg-red-50"
                data-testid="remove-image"
              >
                <XIcon className="w-3 h-3" strokeWidth={2} />
              </button>
            </div>
          ) : (
            <div className="w-24 h-24 bg-gray-100 rounded flex items-center justify-center text-gray-400 text-xs">
              No image
            </div>
          )}
          <label
            data-testid="upload-image-btn"
            className="cursor-pointer px-4 py-2.5 border border-gray-300 hover:bg-gray-50 inline-flex items-center gap-2 text-sm"
          >
            <Upload className="w-4 h-4" strokeWidth={1.5} />
            {uploading ? "..." : (f.image_url ? "Sostituisci" : "Carica immagine")}
            <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="image-file-input" />
          </label>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mt-4">
        {["it", "en", "de"].map((l) => (
          <label key={l} className="block">
            <span className="text-xs uppercase tracking-wider text-[#5C5C59]">Nome ({l.toUpperCase()})</span>
            <input data-testid={`form-name-${l}`} name={`name_${l}`} value={f[`name_${l}`]} onChange={onChange} className={inp + " mt-1"} required />
          </label>
        ))}
      </div>
      <div className="grid sm:grid-cols-3 gap-4 mt-4">
        {["it", "en", "de"].map((l) => (
          <label key={l} className="block">
            <span className="text-xs uppercase tracking-wider text-[#5C5C59]">Descr. ({l.toUpperCase()})</span>
            <textarea name={`description_${l}`} value={f[`description_${l}`]} onChange={onChange} rows="2" className={inp + " mt-1"} />
          </label>
        ))}
      </div>
      <div className="flex gap-3 mt-6">
        <button type="submit" data-testid="form-save" className="px-5 py-2.5 bg-[#8B3A3A] text-white hover:bg-[#6C2A2A]">{t("admin.save")}</button>
        <button type="button" onClick={onClose} className="px-5 py-2.5 border border-gray-300">{t("admin.cancel")}</button>
      </div>
    </form>
  );
}

function ReservationsAdmin({ t }) {
  const [items, setItems] = useState([]);
  const load = () => api.get("/reservations").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    await api.patch(`/reservations/${id}`, { status });
    toast.success("OK");
    load();
  };
  return (
    <div>
      <h1 className="font-serif text-4xl tracking-tight mb-8">{t("admin.reservations")}</h1>
      {items.length === 0 ? (
        <p className="text-[#5C5C59]">{t("admin.empty")}</p>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <div key={r.id} className="bg-white border border-gray-200 rounded p-5 flex flex-wrap items-center gap-4" data-testid={`reservation-row-${r.id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="font-medium">{r.name} · {r.guests}p</div>
                <div className="text-sm text-[#5C5C59]">{r.date} {r.time} · {r.email} · {r.phone}</div>
                {r.notes && <div className="text-xs text-[#5C5C59] mt-1">"{r.notes}"</div>}
              </div>
              <StatusBadge status={r.status} />
              <select
                value={r.status}
                onChange={(e) => setStatus(r.id, e.target.value)}
                data-testid={`reservation-status-${r.id}`}
                className="border border-gray-300 px-3 py-2 rounded text-sm"
              >
                <option value="pending">{t("admin.pending")}</option>
                <option value="confirmed">{t("admin.confirmed")}</option>
                <option value="rejected">{t("admin.rejected")}</option>
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function QuotesAdmin({ t }) {
  const [items, setItems] = useState([]);
  const load = () => api.get("/quotes").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    await api.patch(`/quotes/${id}`, { status });
    toast.success("OK");
    load();
  };
  return (
    <div>
      <h1 className="font-serif text-4xl tracking-tight mb-8">{t("admin.quotes")}</h1>
      {items.length === 0 ? (
        <p className="text-[#5C5C59]">{t("admin.empty")}</p>
      ) : (
        <div className="space-y-3">
          {items.map((q) => (
            <div key={q.id} className="bg-white border border-gray-200 rounded p-5 flex flex-wrap items-center gap-4" data-testid={`quote-row-${q.id}`}>
              <div className="flex-1 min-w-[260px]">
                <div className="font-medium">{q.name} — {q.event_type} · {q.guests}p</div>
                <div className="text-sm text-[#5C5C59]">{q.date} · {q.email} · {q.phone}</div>
                <div className="text-xs text-[#5C5C59] mt-1">Budget: {q.budget || "—"} · {q.preferences || "—"}</div>
              </div>
              <StatusBadge status={q.status} />
              <select
                value={q.status}
                onChange={(e) => setStatus(q.id, e.target.value)}
                data-testid={`quote-status-${q.id}`}
                className="border border-gray-300 px-3 py-2 rounded text-sm"
              >
                <option value="pending">{t("admin.pending")}</option>
                <option value="confirmed">{t("admin.confirmed")}</option>
                <option value="rejected">{t("admin.rejected")}</option>
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CareersAdmin({ t }) {
  const [items, setItems] = useState([]);
  const load = () => api.get("/job-applications").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    await api.patch(`/job-applications/${id}`, { status });
    toast.success("OK");
    load();
  };
  const onDelete = async (id) => {
    if (!window.confirm(t("admin.confirm"))) return;
    await api.delete(`/job-applications/${id}`);
    toast.success("OK");
    load();
  };
  return (
    <div data-testid="careers-admin">
      <h1 className="font-serif text-4xl tracking-tight mb-8">{t("admin.careers")}</h1>
      {items.length === 0 ? (
        <p className="text-[#5C5C59]">{t("admin.empty")}</p>
      ) : (
        <div className="space-y-3">
          {items.map((c) => (
            <div key={c.id} className="bg-white border border-gray-200 rounded p-5 flex flex-wrap items-center gap-4" data-testid={`career-row-${c.id}`}>
              <div className="flex-1 min-w-[260px]">
                <div className="font-medium">{c.name} — {c.position}</div>
                <div className="text-sm text-[#5C5C59]">{c.email} · {c.phone}</div>
                {c.message && <div className="text-xs text-[#5C5C59] mt-1">"{c.message}"</div>}
                {c.cv_url && (
                  <a
                    href={`${API.replace(/\/api$/, "")}${c.cv_url}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`career-cv-${c.id}`}
                    className="inline-flex items-center gap-1 text-xs text-[#8B3A3A] hover:underline mt-1"
                  >
                    <ExternalLink className="w-3 h-3" strokeWidth={1.5} /> CV
                  </a>
                )}
              </div>
              <StatusBadge status={c.status} />
              <select
                value={c.status}
                onChange={(e) => setStatus(c.id, e.target.value)}
                data-testid={`career-status-${c.id}`}
                className="border border-gray-300 px-3 py-2 rounded text-sm"
              >
                <option value="pending">{t("admin.pending")}</option>
                <option value="confirmed">{t("admin.confirmed")}</option>
                <option value="rejected">{t("admin.rejected")}</option>
              </select>
              <button
                onClick={() => onDelete(c.id)}
                data-testid={`career-delete-${c.id}`}
                className="p-2 hover:bg-red-50 text-red-700"
              >
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ContentAdmin() {
  const { refresh } = useContent();
  const [items, setItems] = useState([]);
  const [edits, setEdits] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    api.get("/content").then((r) => setItems(r.data));
  }, []);

  const onChange = (key, lang, value) => {
    setEdits((p) => ({ ...p, [key]: { ...(p[key] || {}), [lang]: value } }));
  };

  const onSave = async (it) => {
    const patch = edits[it.key];
    if (!patch) return;
    setSavingKey(it.key);
    try {
      await api.patch(`/content/${encodeURIComponent(it.key)}`, patch);
      // update local state
      setItems((arr) => arr.map((x) => (x.key === it.key ? { ...x, ...patch } : x)));
      setEdits((p) => { const c = { ...p }; delete c[it.key]; return c; });
      await refresh();
      toast.success("Contenuto aggiornato");
    } catch (e) {
      toast.error("Errore");
    } finally {
      setSavingKey(null);
    }
  };

  // group by group
  const groups = {};
  items.forEach((it) => {
    const g = it.group || "altro";
    groups[g] = groups[g] || [];
    groups[g].push(it);
  });
  const groupLabels = {
    hero: "Pagina Home · Hero",
    about: "Pagina Home · Chi siamo",
    menu: "Pagina Menu",
    reservation: "Pagina Prenotazione",
    quote: "Pagina Eventi",
    location: "Pagina Dove Siamo",
    vicenza: "Pagina Da Vedere",
    careers: "Pagina Lavora con noi",
  };

  return (
    <div data-testid="content-admin">
      <h1 className="font-serif text-4xl tracking-tight mb-2">Contenuti del sito</h1>
      <p className="text-sm text-[#5C5C59] mb-8">
        Modifica titoli, sottotitoli e testi delle pagine in 5 lingue (IT · EN · DE · FR · ES). Le modifiche sono visibili immediatamente. Se lasci vuoto FR o ES, il sito mostrerà automaticamente la versione italiana.
      </p>

      {Object.entries(groups).map(([g, list]) => (
        <section key={g} className="mb-10">
          <h2 className="font-serif text-2xl tracking-tight mb-4">{groupLabels[g] || g}</h2>
          <div className="space-y-4">
            {list.map((it) => {
              const cur = { ...it, ...(edits[it.key] || {}) };
              const isEdited = !!edits[it.key];
              return (
                <div key={it.key} className="bg-white border border-gray-200 rounded p-5" data-testid={`content-item-${it.key}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="text-sm font-medium">{it.label}</div>
                      <div className="text-xs text-gray-400 font-mono mt-0.5">{it.key}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => onSave(it)}
                      disabled={!isEdited || savingKey === it.key}
                      data-testid={`content-save-${it.key}`}
                      className="px-4 py-2 text-sm bg-[#8B3A3A] text-white hover:bg-[#6C2A2A] disabled:opacity-40"
                    >
                      {savingKey === it.key ? "..." : "Salva"}
                    </button>
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-4">
                    {["it", "en", "de", "fr", "es"].map((lang) => {
                      const flag = { it: "🇮🇹", en: "🇬🇧", de: "🇩🇪", fr: "🇫🇷", es: "🇪🇸" }[lang];
                      return (
                        <label key={lang} className="block">
                          <span className="text-xs uppercase tracking-wider text-[#5C5C59] flex items-center gap-1.5 mb-1">
                            {flag} {lang.toUpperCase()}
                          </span>
                          <RichEditor
                            value={cur[lang] || ""}
                            onChange={(v) => onChange(it.key, lang, v)}
                            testId={`content-${it.key}-${lang}`}
                          />
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

function GalleryAdmin() {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/gallery").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data: up } = await api.post("/upload", fd);
        await api.post("/gallery", { image_url: up.url, caption: "", order: items.length });
      }
      toast.success(`${files.length} foto aggiunte`);
      e.target.value = "";
      load();
    } catch (err) {
      toast.error("Errore upload");
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Eliminare questa foto?")) return;
    await api.delete(`/gallery/${id}`);
    toast.success("Foto eliminata");
    load();
  };

  return (
    <div data-testid="gallery-admin">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="font-serif text-4xl tracking-tight">Galleria home</h1>
          <p className="text-sm text-[#5C5C59] mt-2">
            Foto piatti mostrate nella sezione "Piatti dal nostro menu" della home.
            Se non carichi nessuna foto, vengono mostrate immagini di default.
          </p>
        </div>
        <label
          data-testid="gallery-upload-label"
          className="cursor-pointer px-5 py-3 bg-[#8B3A3A] text-white hover:bg-[#6C2A2A] inline-flex items-center gap-2 text-sm whitespace-nowrap"
        >
          <Upload className="w-4 h-4" strokeWidth={1.5} />
          {uploading ? "Caricamento..." : "Carica foto (multiple)"}
          <input
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={onFile}
            disabled={uploading}
            data-testid="gallery-file-input"
          />
        </label>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded p-10 text-center">
          <p className="text-[#5C5C59]">Nessuna foto caricata. Le immagini di default sono visibili sulla home finché non aggiungi le tue.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((g) => (
            <div key={g.id} data-testid={`gallery-row-${g.id}`} className="relative group bg-white border border-gray-200 overflow-hidden">
              <img
                src={resolveImg(g.image_url)}
                alt=""
                className="w-full aspect-square object-cover"
              />
              <button
                onClick={() => onDelete(g.id)}
                data-testid={`gallery-delete-${g.id}`}
                className="absolute top-2 right-2 p-2 bg-white/95 hover:bg-red-50 text-red-700 shadow-md rounded opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: "bg-amber-50 text-amber-800 border-amber-200",
    confirmed: "bg-emerald-50 text-emerald-800 border-emerald-200",
    rejected: "bg-red-50 text-red-800 border-red-200",
  };
  return (
    <span className={`text-xs uppercase tracking-wider px-2.5 py-1 border rounded ${map[status] || ""}`}>
      {status}
    </span>
  );
}


function SummaryAdmin() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/summary").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-[#5C5C59]">Caricamento...</div>;
  const cards = [
    { label: "Visite ultime 24h", value: data.page_views_24h, hint: "vai a Statistiche per dettagli", testid: "summary-views" },
    { label: "Prenotazioni totali", value: data.reservations.total, hint: `${data.reservations.pending} in attesa`, testid: "summary-reservations" },
    { label: "Preventivi eventi", value: data.quotes.total, hint: `${data.quotes.pending} in attesa`, testid: "summary-quotes" },
    { label: "Candidature lavoro", value: data.applications.total, hint: `${data.applications.pending} in attesa`, testid: "summary-applications" },
    { label: "Piatti nel menu", value: data.menu_items, hint: "modificabili dal tab Menu", testid: "summary-menu" },
    { label: "Foto galleria", value: data.gallery_photos, hint: "modificabili dal tab Galleria", testid: "summary-gallery" },
  ];
  return (
    <div data-testid="summary-admin">
      <h1 className="font-serif text-4xl tracking-tight mb-2">Riepilogo</h1>
      <p className="text-sm text-[#5C5C59] mb-8">Stato del sito in un colpo d'occhio. Clicca su una sezione per gestirla in dettaglio.</p>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} data-testid={c.testid} className="bg-white border border-gray-200 rounded p-5">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5C59] font-medium">{c.label}</div>
            <div className="font-serif text-4xl tracking-tight mt-2">{c.value}</div>
            <div className="text-xs text-[#A6A6A0] mt-2">{c.hint}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatsAdmin() {
  const [stats, setStats] = useState(null);
  const [days, setDays] = useState(30);
  useEffect(() => {
    api.get(`/admin/stats?days=${days}`).then((r) => setStats(r.data));
  }, [days]);
  if (!stats) return <div className="text-[#5C5C59]">Caricamento...</div>;
  const max = Math.max(1, ...stats.daily.map((d) => d.count));
  const langName = { it: "Italiano", en: "Inglese", de: "Tedesco", fr: "Francese", es: "Spagnolo" };
  const deviceIcon = { mobile: Smartphone, tablet: Tablet, desktop: Monitor };
  const pageLabel = (p) => ({ "/": "Home", "/menu": "Menu", "/prenota": "Prenotazione", "/eventi": "Eventi", "/dove-siamo": "Dove Siamo", "/da-vedere": "Da Vedere a Vicenza", "/lavora-con-noi": "Lavora con noi" })[p] || p;

  return (
    <div data-testid="stats-admin">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-4xl tracking-tight">Statistiche visite</h1>
          <p className="text-sm text-[#5C5C59] mt-2">Dati anonimi raccolti direttamente dal tuo sito. Nessun servizio esterno.</p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          data-testid="stats-period"
          className="border border-gray-300 px-3 py-2 rounded text-sm bg-white"
        >
          <option value={7}>Ultimi 7 giorni</option>
          <option value={30}>Ultimi 30 giorni</option>
          <option value={90}>Ultimi 90 giorni</option>
        </select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-10">
        <div className="bg-white border border-gray-200 rounded p-5" data-testid="stats-total">
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5C59] font-medium">Visualizzazioni totali</div>
          <div className="font-serif text-4xl tracking-tight mt-2">{stats.total_views}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded p-5" data-testid="stats-unique">
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5C59] font-medium">Visitatori unici (stimati)</div>
          <div className="font-serif text-4xl tracking-tight mt-2">{stats.unique_visitors}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded p-5" data-testid="stats-avg">
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5C59] font-medium">Media giornaliera</div>
          <div className="font-serif text-4xl tracking-tight mt-2">
            {(stats.total_views / Math.max(1, stats.daily.length)).toFixed(1)}
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded p-6 mb-10" data-testid="stats-chart">
        <h2 className="font-serif text-2xl tracking-tight mb-5">Visite giornaliere</h2>
        {stats.daily.length === 0 ? (
          <p className="text-sm text-[#5C5C59]">Nessuna visita registrata in questo periodo.</p>
        ) : (
          <div className="flex items-end gap-1 sm:gap-2 h-48 overflow-x-auto">
            {stats.daily.map((d) => (
              <div key={d.date} className="flex flex-col items-center min-w-[18px] flex-1" title={`${d.date}: ${d.count}`}>
                <div
                  className="w-full bg-[#8B3A3A] hover:bg-[#6C2A2A] transition-colors rounded-t"
                  style={{ height: `${(d.count / max) * 100}%`, minHeight: d.count > 0 ? "4px" : "0" }}
                />
                <div className="text-[9px] text-[#A6A6A0] mt-1 rotate-45 origin-left whitespace-nowrap">{d.date.slice(5)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-gray-200 rounded p-6" data-testid="stats-pages">
          <h3 className="font-serif text-xl tracking-tight mb-4">Pagine più viste</h3>
          {stats.top_pages.length === 0 ? <p className="text-sm text-[#5C5C59]">—</p> : (
            <ul className="space-y-2">
              {stats.top_pages.map((p) => (
                <li key={p.page} className="flex items-center justify-between text-sm">
                  <span>{pageLabel(p.page)}</span>
                  <span className="font-medium tabular-nums">{p.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded p-6" data-testid="stats-langs">
          <h3 className="font-serif text-xl tracking-tight mb-4">Lingue</h3>
          {stats.by_language.length === 0 ? <p className="text-sm text-[#5C5C59]">—</p> : (
            <ul className="space-y-2">
              {stats.by_language.map((l) => (
                <li key={l.lang} className="flex items-center justify-between text-sm">
                  <span>{langName[l.lang] || l.lang}</span>
                  <span className="font-medium tabular-nums">{l.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded p-6" data-testid="stats-devices">
          <h3 className="font-serif text-xl tracking-tight mb-4">Dispositivi</h3>
          {stats.by_device.length === 0 ? <p className="text-sm text-[#5C5C59]">—</p> : (
            <ul className="space-y-2">
              {stats.by_device.map((d) => {
                const Icon = deviceIcon[d.device] || Monitor;
                return (
                  <li key={d.device} className="flex items-center justify-between text-sm">
                    <span className="inline-flex items-center gap-2">
                      <Icon className="w-4 h-4" strokeWidth={1.5} /> {d.device}
                    </span>
                    <span className="font-medium tabular-nums">{d.count}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function ReviewsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/reviews/all").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Eliminare questa recensione?")) return;
    await api.delete(`/reviews/${id}`);
    toast.success("Eliminata");
    load();
  };
  const toggle = async (r) => {
    await api.put(`/reviews/${r.id}`, { ...r, is_visible: !r.is_visible });
    toast.success(r.is_visible ? "Nascosta" : "Pubblicata");
    load();
  };

  return (
    <div data-testid="reviews-admin">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="font-serif text-4xl tracking-tight">Recensioni</h1>
          <p className="text-sm text-[#5C5C59] mt-2">Aggiungi, modifica o nascondi le recensioni mostrate sulla home in 5 lingue.</p>
        </div>
        <button
          data-testid="add-review-btn"
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="px-5 py-3 bg-[#8B3A3A] text-white hover:bg-[#6C2A2A] inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" strokeWidth={1.5} /> Aggiungi recensione
        </button>
      </div>

      {showForm && (
        <ReviewForm
          initial={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}

      <div className="space-y-3">
        {items.map((r) => (
          <div key={r.id} data-testid={`review-row-${r.id}`} className="bg-white border border-gray-200 rounded p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[260px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{r.author_name}</span>
                  <span className="text-xs text-[#8B3A3A]">{r.rating}★</span>
                  <span className="text-[10px] uppercase tracking-wider bg-gray-100 px-2 py-0.5 rounded">{r.source}</span>
                  {r.date_iso && <span className="text-xs text-[#A6A6A0]">{r.date_iso}</span>}
                  {!r.is_visible && <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-0.5 rounded">Nascosta</span>}
                </div>
                <p className="text-sm text-[#5C5C59] mt-2 line-clamp-2">{r.text_it}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => toggle(r)} data-testid={`review-toggle-${r.id}`} className="px-3 py-2 text-sm border border-gray-300 hover:bg-gray-50">
                  {r.is_visible ? "Nascondi" : "Pubblica"}
                </button>
                <button onClick={() => { setEditing(r); setShowForm(true); }} data-testid={`review-edit-${r.id}`} className="p-2 hover:bg-gray-100">
                  <Pencil className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <button onClick={() => onDelete(r.id)} data-testid={`review-delete-${r.id}`} className="p-2 hover:bg-red-50 text-red-700">
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReviewForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial || {
    author_name: "", rating: 5, source: "Google", date_iso: new Date().toISOString().slice(0, 10),
    text_it: "", text_en: "", text_de: "", text_fr: "", text_es: "",
    order: 0, is_visible: true,
  });
  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setF((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };
  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...f, rating: Number(f.rating), order: Number(f.order) };
    if (initial) await api.put(`/reviews/${initial.id}`, payload);
    else await api.post("/reviews", payload);
    toast.success("Salvata");
    onSaved();
  };
  const inp = "w-full border border-gray-300 px-3 py-2 rounded text-sm";
  return (
    <form onSubmit={submit} data-testid="review-form" className="bg-white border border-gray-200 rounded p-6 mb-6">
      <div className="grid sm:grid-cols-4 gap-4">
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-[#5C5C59]">Nome</span>
          <input name="author_name" value={f.author_name} onChange={onChange} className={inp + " mt-1"} required data-testid="review-form-name" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-[#5C5C59]">Voto (1-5)</span>
          <input name="rating" type="number" min="1" max="5" step="1" value={f.rating} onChange={onChange} className={inp + " mt-1"} required />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-[#5C5C59]">Fonte</span>
          <select name="source" value={f.source} onChange={onChange} className={inp + " mt-1"}>
            <option>Google</option>
            <option>TripAdvisor</option>
          </select>
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-[#5C5C59]">Data</span>
          <input name="date_iso" type="date" value={f.date_iso} onChange={onChange} className={inp + " mt-1"} />
        </label>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        {["it", "en", "de", "fr", "es"].map((l) => (
          <label key={l} className="block">
            <span className="text-xs uppercase tracking-wider text-[#5C5C59]">Testo ({l.toUpperCase()})</span>
            <textarea name={`text_${l}`} value={f[`text_${l}`] || ""} onChange={onChange} rows="3" className={inp + " mt-1"} required={l === "it"} />
          </label>
        ))}
      </div>
      <div className="flex items-center gap-4 mt-4">
        <label className="flex items-center gap-2">
          <input type="checkbox" name="is_visible" checked={f.is_visible} onChange={onChange} />
          <span className="text-sm">Visibile sul sito</span>
        </label>
        <label className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-[#5C5C59]">Ordine</span>
          <input name="order" type="number" value={f.order} onChange={onChange} className="w-20 border border-gray-300 px-2 py-1 rounded text-sm" />
        </label>
      </div>
      <div className="flex gap-3 mt-6">
        <button type="submit" data-testid="review-save" className="px-5 py-2.5 bg-[#8B3A3A] text-white hover:bg-[#6C2A2A]">Salva</button>
        <button type="button" onClick={onClose} className="px-5 py-2.5 border border-gray-300">Annulla</button>
      </div>
    </form>
  );
}


const SITE_IMAGE_SLOTS = [
  { key: "home.hero", label: "Foto Hero (sezione grande in alto della home)", aspect: "aspect-[16/9]" },
  { key: "home.about", label: "Foto sezione 'Chi siamo' (verticale a sinistra)", aspect: "aspect-[4/5]" },
  { key: "home.cta", label: "Sfondo sezione 'Eventi privati' (CTA scura)", aspect: "aspect-[16/9]" },
  { key: "home.city", label: "Foto sezione 'Vicenza, città del Palladio'", aspect: "aspect-[5/4]" },
  { key: "header.bg", label: "Sfondo dell'header (immagine sfocata in alto)", aspect: "aspect-[16/4]" },
];

function SiteImagesAdmin() {
  const [images, setImages] = useState({});
  const [uploadingKey, setUploadingKey] = useState(null);

  const load = () => api.get("/site-images").then((r) => setImages(r.data || {}));
  useEffect(() => { load(); }, []);

  const onUpload = async (key, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 10MB)");
      return;
    }
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/upload", fd);
      await api.put(`/site-images/${encodeURIComponent(key)}`, { key, url: up.url });
      toast.success("Immagine aggiornata");
      load();
    } catch (e) {
      toast.error("Errore upload");
    } finally {
      setUploadingKey(null);
    }
  };

  const onReset = async (key) => {
    if (!window.confirm("Ripristinare l'immagine di default per questa sezione?")) return;
    await api.delete(`/site-images/${encodeURIComponent(key)}`);
    toast.success("Ripristinata");
    load();
  };

  const resolved = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    return `${API.replace(/\/api$/, "")}${url}`;
  };

  return (
    <div data-testid="site-images-admin">
      <h1 className="font-serif text-4xl tracking-tight mb-2">Immagini del sito</h1>
      <p className="text-sm text-[#5C5C59] mb-8">
        Sostituisci le foto principali delle sezioni del sito. Le modifiche sono visibili immediatamente. Lascia vuoto per usare l'immagine di default.
      </p>

      <div className="grid md:grid-cols-2 gap-5">
        {SITE_IMAGE_SLOTS.map((slot) => {
          const url = resolved(images[slot.key]);
          return (
            <div key={slot.key} className="bg-white border border-gray-200 rounded p-5" data-testid={`site-image-${slot.key}`}>
              <div className="text-sm font-medium">{slot.label}</div>
              <div className="text-xs text-gray-400 font-mono mt-0.5 mb-3">{slot.key}</div>
              <div className={`relative w-full ${slot.aspect} bg-[#E5E3D8] overflow-hidden mb-4`}>
                {url ? (
                  <img src={url} alt={slot.label} className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-[#A6A6A0] text-xs">
                    Immagine di default
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <label className="inline-flex items-center gap-2 px-4 py-2 bg-[#8B3A3A] text-white hover:bg-[#6C2A2A] cursor-pointer text-sm">
                  <Upload className="w-4 h-4" strokeWidth={1.5} />
                  {uploadingKey === slot.key ? "Caricamento..." : "Carica nuova"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid={`upload-${slot.key}`}
                    onChange={(e) => onUpload(slot.key, e.target.files?.[0])}
                  />
                </label>
                {images[slot.key] && (
                  <button
                    onClick={() => onReset(slot.key)}
                    data-testid={`reset-${slot.key}`}
                    className="px-4 py-2 text-sm border border-gray-300 hover:bg-gray-50"
                  >
                    Ripristina default
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);

function CategoriesAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/categories/all").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Eliminare questa categoria? (possibile solo se non ha piatti)")) return;
    try {
      await api.delete(`/categories/${id}`);
      toast.success("Eliminata");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const toggleVisible = async (c) => {
    await api.put(`/categories/${c.id}`, { ...c, is_visible: !c.is_visible });
    toast.success(c.is_visible ? "Nascosta" : "Pubblicata");
    load();
  };

  return (
    <div data-testid="categories-admin">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="font-serif text-4xl tracking-tight">Categorie del menu</h1>
          <p className="text-sm text-[#5C5C59] mt-2">
            Crea, ordina o nascondi le categorie. Ogni piatto deve appartenere a una categoria.
          </p>
        </div>
        <button
          data-testid="add-category-btn"
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="px-5 py-3 bg-[#8B3A3A] text-white hover:bg-[#6C2A2A] inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" strokeWidth={1.5} /> Aggiungi categoria
        </button>
      </div>

      {showForm && (
        <CategoryForm
          initial={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}

      <div className="bg-white border border-gray-200 rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-gray-200 text-xs tracking-wider uppercase text-[#5C5C59]">
            <tr>
              <th className="px-4 py-3 text-left w-16">Ord.</th>
              <th className="px-4 py-3 text-left">Chiave</th>
              <th className="px-4 py-3 text-left">IT</th>
              <th className="px-4 py-3 text-left">EN</th>
              <th className="px-4 py-3 text-left">DE</th>
              <th className="px-4 py-3 text-center">Visibile</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-b border-gray-100" data-testid={`category-row-${c.id}`}>
                <td className="px-4 py-3 tabular-nums text-[#5C5C59]">{c.order}</td>
                <td className="px-4 py-3 font-mono text-xs text-gray-500">{c.key}</td>
                <td className="px-4 py-3 font-medium">{c.label_it}</td>
                <td className="px-4 py-3 text-[#5C5C59]">{c.label_en}</td>
                <td className="px-4 py-3 text-[#5C5C59]">{c.label_de}</td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() => toggleVisible(c)}
                    data-testid={`category-toggle-${c.id}`}
                    className={`px-2 py-1 text-xs rounded ${c.is_visible ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-700"}`}
                  >
                    {c.is_visible ? "Visibile" : "Nascosta"}
                  </button>
                </td>
                <td className="px-4 py-3 flex gap-2 justify-end">
                  <button onClick={() => { setEditing(c); setShowForm(true); }} data-testid={`category-edit-${c.id}`} className="p-2 hover:bg-gray-100">
                    <Pencil className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                  <button onClick={() => onDelete(c.id)} data-testid={`category-delete-${c.id}`} className="p-2 hover:bg-red-50 text-red-700">
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CategoryForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial || {
    key: "", label_it: "", label_en: "", label_de: "", label_fr: "", label_es: "",
    order: 100, is_visible: true,
  });
  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setF((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };
  const onLabelItChange = (e) => {
    setF((p) => ({ ...p, label_it: e.target.value, key: initial ? p.key : slugify(e.target.value) }));
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!f.key) {
      toast.error("Inserisci nome e chiave");
      return;
    }
    const payload = { ...f, order: Number(f.order) };
    try {
      if (initial) await api.put(`/categories/${initial.id}`, payload);
      else await api.post("/categories", payload);
      toast.success("Salvata");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };
  const inp = "w-full border border-gray-300 px-3 py-2 rounded text-sm";
  return (
    <form onSubmit={submit} data-testid="category-form" className="bg-white border border-gray-200 rounded p-6 mb-6">
      <div className="grid sm:grid-cols-3 gap-4">
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-[#5C5C59]">Nome (IT) *</span>
          <input name="label_it" value={f.label_it} onChange={onLabelItChange} className={inp + " mt-1"} required placeholder="Es. Antipasti di Mare" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-[#5C5C59]">Chiave (auto-generata)</span>
          <input name="key" value={f.key} onChange={onChange} className={inp + " mt-1 font-mono"} required disabled={!!initial} placeholder="antipasti_mare" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-[#5C5C59]">Ordine</span>
          <input name="order" type="number" value={f.order} onChange={onChange} className={inp + " mt-1"} />
        </label>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        {[["en", "🇬🇧 EN"], ["de", "🇩🇪 DE"], ["fr", "🇫🇷 FR"], ["es", "🇪🇸 ES"]].map(([k, lbl]) => (
          <label key={k} className="block">
            <span className="text-xs uppercase tracking-wider text-[#5C5C59]">{lbl}</span>
            <input name={`label_${k}`} value={f[`label_${k}`] || ""} onChange={onChange} className={inp + " mt-1"} placeholder="(opzionale)" />
          </label>
        ))}
      </div>
      <div className="flex items-center gap-4 mt-4">
        <label className="flex items-center gap-2">
          <input type="checkbox" name="is_visible" checked={f.is_visible} onChange={onChange} />
          <span className="text-sm">Visibile sul sito</span>
        </label>
      </div>
      <div className="flex gap-3 mt-6">
        <button type="submit" data-testid="category-save" className="px-5 py-2.5 bg-[#8B3A3A] text-white hover:bg-[#6C2A2A]">Salva</button>
        <button type="button" onClick={onClose} className="px-5 py-2.5 border border-gray-300">Annulla</button>
      </div>
    </form>
  );
}

